import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { setHeaderText } from "../../features/headerText/headerTextSlice";
import { isMvpEnv } from "../../utils/brandLogo";
import userTypeConsts from "../../constants/userTypeConsts";
import Currencies from "../../constants/currenciesList";
import { usePosition } from "../../components/TimeTracker/TimeTrackerClient/useClientDetails";
import { useFetchProjectManager } from "../../components/TimeTracker/TimeTrackerProject/useProjectDetails";
import LeftArrow from "../../images/icons/blue-left-arrow.svg";
import profileImage from "../../images/user/Default_pfp.png";
import SingleUser from "../../images/icons/single-user-icon.svg";
import EmailIcon from "../../images/icons/email.svg";
import CompanyUrl from "../../images/icons/webpage.svg";
import LocationIcon from "../../images/icons/location.svg";
import PostalIcon from "../../images/icons/postal-code.svg";
import PassportIcon from "../../images/icons/passport.svg";
import PaymentIcon from "../../images/icons/payment.svg";
import PhoneIcon from "../../images/icons/phone-icon.svg";
import CostIcon from "../../images/icons/cost1.svg";
import CompanyIcon from "../../images/icons/company-type.svg";
import WhiteTick from "../../images/icons/tick-arrow.svg";
import "./MvpRegisterClient.css";

const PAYMENT_METHODS = ["Bank Transfer", "Wise Transfer", "Card"];

function userLabel(person) {
  if (!person) return "";
  if (person.first_name) {
    const middle = person.middle_name && person.middle_name !== "null" ? person.middle_name : "";
    return `${person.first_name} ${middle} ${person.last_name || ""}`.replace(/\s+/g, " ").trim();
  }
  if (person.company_first_name) {
    return `${person.company_first_name} ${person.company_last_name || ""}`.trim();
  }
  return person.company_name || "";
}

function Field({ icon, label, ...inputProps }) {
  return (
    <label className="mvp-reg-field">
      <img className="mvp-reg-icon" src={icon} alt="" />
      <span className="mvp-reg-control">
        <span className="mvp-reg-label">{label}</span>
        <input className="mvp-reg-input" {...inputProps} />
      </span>
    </label>
  );
}

function SelectField({ icon, label, placeholder, value, options, open, onToggle, onSelect, searchable, search, onSearch }) {
  const shown = options.filter((option) =>
    option.label.toLowerCase().includes((search || "").toLowerCase())
  );
  const menu = (
    <div className={`custom-dropdown-user-new-outer mvp-reg-select ${open ? "open" : ""}`}>
        <button type="button" className={`custom-dropdown-user-new mvp-reg-trigger ${value ? "" : "is-placeholder"}`} onClick={onToggle}>
          {value || placeholder}
        </button>
        {open && (
          <div className="custom-dropdown-new mvp-reg-menu">
            {searchable && (
              <input
                type="text"
                className="search-bar"
                placeholder="Search..."
                value={search}
                onChange={(event) => onSearch(event.target.value)}
              />
            )}
            <ul>
              {shown.map((option) => (
                <li key={option.value} className={option.value === value ? "selected-item" : ""} onClick={() => onSelect(option.value)}>
                  {option.label}
                </li>
              ))}
              {shown.length === 0 && <li className="mvp-reg-empty">No results</li>}
            </ul>
          </div>
        )}
    </div>
  );

  if (!icon) return menu;

  return (
    <div className="mvp-reg-field">
      <img className="mvp-reg-icon" src={icon} alt="" />
      <span className="mvp-reg-control">
        {label ? <span className="mvp-reg-label">{label}</span> : null}
        {menu}
      </span>
    </div>
  );
}

export default function MvpRegisterClient() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;
  const canManage =
    isMvpEnv() &&
    user &&
    (user.userType === userTypeConsts.SUPER_ADMIN || user.userType === userTypeConsts.ADMIN);

  const { data: managers = [] } = useFetchProjectManager(token);
  const { data: positions = [] } = usePosition(token);
  const fileRef = useRef(null);
  const [openMenu, setOpenMenu] = useState("");
  const [managerSearch, setManagerSearch] = useState("");
  const [currencySearch, setCurrencySearch] = useState("");
  const [logoPreview, setLogoPreview] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    clientName: "",
    legalEntityName: "",
    email: "",
    companyWebsite: "",
    city: "",
    country: "",
    address: "",
    pincode: "",
    vatNumber: "",
    vatPercentage: "",
    preferredCurrency: "",
    paymentMethod: "",
    billingContactName: "",
    billingContactPosition: "",
    billingContactEmail: "",
    billingMobileNo: "",
    accManager: "",
    signatoryUserName: "",
    signatoryEmail: "",
    signatoryUserPosition: "",
    signatoryMobileNo: "",
    pocUserName: "",
    pocEmail: "",
    pocUserPosition: "",
  });

  useEffect(() => {
    dispatch(setHeaderText("Clients"));
  }, [dispatch]);

  useEffect(() => {
    if (!canManage) navigate("/client/overview", { replace: true });
  }, [canManage, navigate]);

  useEffect(() => {
    const close = (event) => {
      if (!event.target.closest(".mvp-reg-select")) setOpenMenu("");
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const choose = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setOpenMenu("");
  };

  const managerOptions = useMemo(
    () => managers.map((person) => ({ value: person._id, label: userLabel(person) })).filter((option) => option.label),
    [managers]
  );
  const currencyOptions = useMemo(
    () =>
      (Currencies.sourceCurrencies?.[0]?.targetCurrencies || []).map((currency) => ({
        value: currency.currencyCode,
        label: `${currency.currencyCode} (${currency.currencyName})`,
      })),
    []
  );
  const positionOptions = useMemo(
    () =>
      (Array.isArray(positions) ? positions : [])
        .map((position) => ({
          value: position.position_name || position.name || "",
          label: position.position_name || position.name || "",
        }))
        .filter((option) => option.value),
    [positions]
  );
  const paymentOptions = PAYMENT_METHODS.map((method) => ({ value: method, label: method }));
  const managerLabel = managerOptions.find((option) => option.value === form.accManager)?.label || "";

  const onLogo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const submit = async (event) => {
    event.preventDefault();
    const name = form.clientName.trim();
    const email = form.email.trim();
    if (!name) {
      toast.error("Commercial name is required", { position: "bottom-right", autoClose: 2000 });
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("A valid email address is required", { position: "bottom-right", autoClose: 2000 });
      return;
    }
    if (form.companyWebsite.trim() && !/[.]/.test(form.companyWebsite.trim())) {
      toast.error("Enter a valid company URL", { position: "bottom-right", autoClose: 2000 });
      return;
    }

    const payload = {
      client_name: name,
      legal_entity_name: form.legalEntityName.trim(),
      email,
      mobile_no: form.billingMobileNo.trim(),
      acc_manager: form.accManager,
      address: form.address.trim(),
      country: form.country.trim(),
      city: form.city.trim(),
      pincode: form.pincode.trim(),
      company_website: form.companyWebsite.trim(),
      vat_number: form.vatNumber.trim(),
      vat_percentage: form.vatPercentage,
      billing_contact_name: form.billingContactName.trim(),
      billing_contact_position: form.billingContactPosition,
      billing_contact_email: form.billingContactEmail.trim(),
      billing_mobile_no: form.billingMobileNo.trim(),
      preferred_currency: form.preferredCurrency,
      payment_method: form.paymentMethod,
      signatory_user_name: form.signatoryUserName.trim(),
      signatory_email: form.signatoryEmail.trim(),
      signatory_user_position: form.signatoryUserPosition,
      signatory_mobile_no: form.signatoryMobileNo.trim(),
      poc_user_name: form.pocUserName.trim(),
      poc_email: form.pocEmail.trim(),
      poc_user_position: form.pocUserPosition,
    };

    const body = new FormData();
    if (logoFile) body.append("company_logo", logoFile);
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") body.append(key, value);
    });

    try {
      setSubmitting(true);
      const response = await axios.post("/api/client-details/create", body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data || {};
      if (data.status === "exists" || data.status === "error") {
        toast.error(data.message || "Client could not be created", { position: "bottom-right", autoClose: 2500 });
        return;
      }
      toast.success("Client registered successfully", { position: "bottom-right", autoClose: 2000 });
      navigate("/client/overview");
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong", { position: "bottom-right", autoClose: 2500 });
    } finally {
      setSubmitting(false);
    }
  };

  if (!canManage) return null;

  return (
    <form className="mvp-reg" onSubmit={submit}>
      <button type="button" className="mvp-reg-back" onClick={() => navigate("/client/overview")}>
        <img src={LeftArrow} alt="" />
        Back to All Clients
      </button>

      <div className="mvp-reg-card">
        <div className="mvp-reg-title">
          <h1>Register New Client</h1>
        </div>

        <div className="mvp-reg-hero">
          <div className="mvp-reg-identity">
            <button type="button" className="mvp-reg-avatar" onClick={() => fileRef.current?.click()} aria-label="Upload client logo">
              <img src={logoPreview || profileImage} alt="" />
              <span className="mvp-reg-cam">
                <svg xmlns="http://www.w3.org/2000/svg" width="29" height="29" viewBox="0 0 29 29" fill="none" aria-hidden="true">
                  <circle cx="14.5" cy="14.5" r="14.5" fill="#6479F8" />
                  <path d="M22.7194 10.1524L19.2559 6.66847C19.2063 6.61849 19.1405 6.59104 19.0701 6.59104C18.9998 6.59104 18.9344 6.6185 18.8847 6.66848L16.8328 8.73279L16.8303 8.74018L16.7894 8.77644L8.86392 16.7497C8.82719 16.7866 8.80235 16.8331 8.7922 16.8838L7.91485 21.2513C7.89736 21.3382 7.9243 21.4273 7.98658 21.4903C8.03626 21.5399 8.10204 21.5673 8.17236 21.5673C8.18986 21.5673 8.207 21.5656 8.2231 21.5624L12.5654 20.6792C12.6164 20.669 12.6623 20.644 12.6987 20.6074L22.7194 10.5259C22.7691 10.4762 22.7961 10.4097 22.7961 10.339C22.7961 10.2682 22.7688 10.2021 22.7194 10.1524ZM12.3831 20.1777L8.50718 20.9662L9.29043 17.0668L10.4345 15.9158L13.5271 19.0271L12.3831 20.1777ZM13.898 18.6536L10.8053 15.5427L16.9801 9.33077L20.0724 12.4417L13.8976 18.6536L13.898 18.6536ZM20.4439 12.0682L17.3516 8.95733L19.0702 7.22844L22.1628 10.3393L20.4439 12.0682Z" fill="white" stroke="white" strokeWidth="0.4" />
                </svg>
              </span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onLogo} />
            <input
              className="mvp-reg-client-name"
              placeholder="Client Name"
              value={form.clientName}
              onChange={set("clientName")}
            />
          </div>
          <div className="mvp-reg-actions mvp-reg-actions-mobile">
            <button type="button" className="mvp-reg-cancel" onClick={() => navigate("/client/overview")}>
              Cancel
            </button>
            <button type="submit" className="mvp-reg-create" disabled={submitting}>
              <img src={WhiteTick} alt="" />
              {submitting ? "Creating..." : "Create Client"}
            </button>
          </div>
          <div className="mvp-reg-manager">
            <span>Account Manager</span>
            <SelectField
              placeholder="Assign User"
              value={managerLabel}
              options={managerOptions}
              open={openMenu === "manager"}
              onToggle={() => setOpenMenu(openMenu === "manager" ? "" : "manager")}
              onSelect={(value) => choose("accManager", value)}
              searchable
              search={managerSearch}
              onSearch={setManagerSearch}
            />
          </div>
        </div>

        <section>
          <h2>Company Information</h2>
          <div className="mvp-reg-grid">
            <Field icon={SingleUser} label="Commercial Name" value={form.clientName} onChange={set("clientName")} />
            <Field icon={EmailIcon} label="Email" type="email" value={form.email} onChange={set("email")} />
            <Field icon={CompanyIcon} label="Legal Entity Name" value={form.legalEntityName} onChange={set("legalEntityName")} />
            <Field icon={CompanyUrl} label="Company URL" value={form.companyWebsite} onChange={set("companyWebsite")} />
          </div>
        </section>

        <section>
          <h2>Addresses</h2>
          <div className="mvp-reg-grid">
            <Field icon={LocationIcon} label="City" value={form.city} onChange={set("city")} />
            <Field icon={LocationIcon} label="Address" value={form.address} onChange={set("address")} />
            <Field icon={LocationIcon} label="Country" value={form.country} onChange={set("country")} />
            <Field icon={PostalIcon} label="Postal Code" value={form.pincode} onChange={set("pincode")} />
          </div>
        </section>

        <section>
          <h2>Billing Information</h2>
          <div className="mvp-reg-grid">
            <Field icon={PassportIcon} label="Company VAT Number" value={form.vatNumber} onChange={set("vatNumber")} />
            <Field icon={SingleUser} label="Billing contact: Full Name" value={form.billingContactName} onChange={set("billingContactName")} />
            <Field icon={CostIcon} label="Company VAT applicable Percentage" value={form.vatPercentage} onChange={set("vatPercentage")} />
            {positionOptions.length > 0 ? (
              <SelectField
                icon={CompanyIcon}
                label="Billing contact: Position"
                placeholder="Select"
                value={form.billingContactPosition}
                options={positionOptions}
                open={openMenu === "billPos"}
                onToggle={() => setOpenMenu(openMenu === "billPos" ? "" : "billPos")}
                onSelect={(value) => choose("billingContactPosition", value)}
              />
            ) : (
              <Field icon={CompanyIcon} label="Billing contact: Position" value={form.billingContactPosition} onChange={set("billingContactPosition")} />
            )}
            <SelectField
              icon={PaymentIcon}
              label="Preferred Currency"
              placeholder="Select"
              value={form.preferredCurrency}
              options={currencyOptions}
              open={openMenu === "currency"}
              onToggle={() => setOpenMenu(openMenu === "currency" ? "" : "currency")}
              onSelect={(value) => choose("preferredCurrency", value)}
              searchable
              search={currencySearch}
              onSearch={setCurrencySearch}
            />
            <Field icon={EmailIcon} label="Billing contact: Email Address" type="email" value={form.billingContactEmail} onChange={set("billingContactEmail")} />
            <SelectField
              icon={PaymentIcon}
              label="Preferred Payment Method"
              placeholder="Select"
              value={form.paymentMethod}
              options={paymentOptions}
              open={openMenu === "payment"}
              onToggle={() => setOpenMenu(openMenu === "payment" ? "" : "payment")}
              onSelect={(value) => choose("paymentMethod", value)}
            />
            <Field icon={PhoneIcon} label="Contact Number" value={form.billingMobileNo} onChange={set("billingMobileNo")} />
          </div>
        </section>

        <section>
          <h2>Signatory Information</h2>
          <div className="mvp-reg-grid">
            <Field icon={SingleUser} label="Full Name" value={form.signatoryUserName} onChange={set("signatoryUserName")} />
            <Field icon={EmailIcon} label="Email" type="email" value={form.signatoryEmail} onChange={set("signatoryEmail")} />
            {positionOptions.length > 0 ? (
              <SelectField
                icon={CompanyIcon}
                label="Position"
                placeholder="Select"
                value={form.signatoryUserPosition}
                options={positionOptions}
                open={openMenu === "signPos"}
                onToggle={() => setOpenMenu(openMenu === "signPos" ? "" : "signPos")}
                onSelect={(value) => choose("signatoryUserPosition", value)}
              />
            ) : (
              <Field icon={CompanyIcon} label="Position" value={form.signatoryUserPosition} onChange={set("signatoryUserPosition")} />
            )}
            <Field icon={PhoneIcon} label="Contact Number" value={form.signatoryMobileNo} onChange={set("signatoryMobileNo")} />
          </div>
        </section>

        <section>
          <h2>Point of Contact</h2>
          <div className="mvp-reg-grid">
            <Field icon={SingleUser} label="Full Name" value={form.pocUserName} onChange={set("pocUserName")} />
            <Field icon={EmailIcon} label="Email" type="email" value={form.pocEmail} onChange={set("pocEmail")} />
            {positionOptions.length > 0 ? (
              <SelectField
                icon={CompanyIcon}
                label="Position"
                placeholder="Select"
                value={form.pocUserPosition}
                options={positionOptions}
                open={openMenu === "pocPos"}
                onToggle={() => setOpenMenu(openMenu === "pocPos" ? "" : "pocPos")}
                onSelect={(value) => choose("pocUserPosition", value)}
              />
            ) : (
              <Field icon={CompanyIcon} label="Position" value={form.pocUserPosition} onChange={set("pocUserPosition")} />
            )}
          </div>
        </section>
      </div>

      <div className="mvp-reg-actions mvp-reg-actions-desktop">
        <button type="button" className="mvp-reg-cancel" onClick={() => navigate("/client/overview")}>
          Cancel
        </button>
        <button type="submit" className="mvp-reg-create" disabled={submitting}>
          <img src={WhiteTick} alt="" />
          {submitting ? "Creating..." : "Create Client"}
        </button>
      </div>
    </form>
  );
}
